import { Injectable } from '@angular/core';

export interface UrlMapping {
  angularPath: string;
  reactPath: string;
  queryParams: Record<string, string>;
}

/**
 * Service to handle bidirectional URL mapping between Angular routes and React query params
 * Specifically for vehicles, warehouses, trailers, and jobsites pages
 */
@Injectable({
  providedIn: 'root',
})
export class AngularReactUrlMapperService {
  private readonly urlMappings: UrlMapping[] = [
    // Warehouse mappings - old routes
    { angularPath: '/map/warehouses/active', reactPath: '/warehouses', queryParams: {} },
    { angularPath: '/map/warehouses/recent', reactPath: '/warehouses', queryParams: {} },
    { angularPath: '/map/warehouses/inactive', reactPath: '/warehouses', queryParams: {} },
    { angularPath: '/map/warehouses', reactPath: '/warehouses', queryParams: {} },
    
    // Warehouse mappings - new routes
    { angularPath: '/warehouses', reactPath: '/warehouses', queryParams: {} },
    
    // Vehicle mappings - old routes
    { angularPath: '/map/vehicles/tracked', reactPath: '/vehicles', queryParams: {} },
    { angularPath: '/map/vehicles/untracked', reactPath: '/vehicles', queryParams: {} },
    { angularPath: '/map/vehicles', reactPath: '/vehicles', queryParams: {} },
    
    // Vehicle mappings - new routes
    { angularPath: '/vehicles', reactPath: '/vehicles', queryParams: {} },
    
    // Trailer mappings - old routes
    { angularPath: '/map/trailers/tracked', reactPath: '/trailers', queryParams: {} },
    { angularPath: '/map/trailers/untracked', reactPath: '/trailers', queryParams: {} },
    { angularPath: '/map/trailers', reactPath: '/trailers', queryParams: {} },
    
    // Trailer mappings - new routes
    { angularPath: '/trailers', reactPath: '/trailers', queryParams: {} },
    
    // Jobsite (remote locations) mappings - old routes
    { angularPath: '/map/locations/active', reactPath: '/jobsites', queryParams: {} },
    { angularPath: '/map/locations/recent', reactPath: '/jobsites', queryParams: {} },
    { angularPath: '/map/locations/inactive', reactPath: '/jobsites', queryParams: {} },
    { angularPath: '/map/locations/unconfirmed', reactPath: '/jobsites', queryParams: {} },
    { angularPath: '/map/locations', reactPath: '/jobsites', queryParams: {} },
    
    // Jobsite mappings - new routes
    { angularPath: '/jobsites', reactPath: '/jobsites', queryParams: {} },
  ];

  /**
   * Converts an Angular path to a React path with query parameters
   * @param angularUrl The Angular route URL (may include query params)
   * @returns The React path with query parameters
   */
  angularToReact(angularUrl: string): string {
    // Parse the URL to separate path and query params
    const [path, existingQueryString] = angularUrl.split('?');
    
    // Find mapping based on path
    const mapping = this.urlMappings.find(m => m.angularPath === path);
    if (!mapping) {
      return angularUrl; // Return original URL if no mapping found
    }

    // Preserve existing query params if any
    const queryString = existingQueryString ? `?${existingQueryString}` : '';
    return mapping.reactPath + queryString;
  }

  /**
   * Converts a React path with query parameters to an Angular path
   * @param reactUrl The React URL with query parameters
   * @returns The Angular route path
   */
  reactToAngular(reactUrl: string): string {
    const url = new URL(reactUrl, 'http://placeholder.com'); // Use placeholder for parsing
    const pathname = url.pathname;

    // Find matching mapping based on path - use the first matching angular path
    // Prefer non-map routes for new navigation
    const mapping = this.urlMappings.find(m => 
      m.reactPath === pathname && !m.angularPath.startsWith('/map/')
    ) || this.urlMappings.find(m => m.reactPath === pathname);

    return mapping ? mapping.angularPath : pathname;
  }

  /**
   * Checks if a given path should use React rendering
   * @param path The path to check
   * @returns True if the path should use React rendering
   */
  shouldUseReact(path: string): boolean {
    return this.urlMappings.some(m => path.startsWith(m.angularPath));
  }
}
